import React, { useState } from 'react';
import { IconAdjustmentsHorizontal, IconStethoscope } from '@tabler/icons-react';
import PublicHeader from '../../components/layout/PublicHeader';
import BottomNav from '../../components/layout/BottomNav';
import StickyFooter from '../../components/layout/StickyFooter';
import {
  Button, Chips, SearchField, Skeleton, EmptyState, ErrorState, BottomSheet,
} from '../../components/common';
import DoctorListCard from '../../components/booking/DoctorListCard';
import doctorApi from '../../api/doctor/doctorApi';
import useFetch from '../../hooks/useFetch';
import useDebounce from '../../hooks/useDebounce';
import { useAuth } from '../../context/AuthContext';
import { PATHS } from '../../routes/paths';
import { pluralize } from '../../utils/format';

const SORTS = [
  { key: 'earliest', label: 'Earliest available' },
  { key: 'name', label: 'Name (A–Z)' },
];

/**
 * Pre-login: "Our doctors" with a "Log in to book" footer.
 * Logged-in: "Find a doctor" with Book buttons and the tab bar.
 */
export default function DoctorsPage() {
  const { isAuthenticated } = useAuth();
  const [query, setQuery] = useState('');
  const [specialty, setSpecialty] = useState('');
  const [sort, setSort] = useState('earliest');
  const [sortOpen, setSortOpen] = useState(false);
  const q = useDebounce(query);

  const { data: specialties } = useFetch(() => doctorApi.specialties(), [], { initial: [] });
  const { data: doctors, loading, error, reload } = useFetch(
    () => doctorApi.list({ q, specialty }), [q, specialty], { initial: [] },
  );

  const sorted = [...doctors].sort((a, b) => {
    if (sort === 'name') return a.name.localeCompare(b.name);
    const ka = a.nextAvailable ? `${a.nextAvailable.date}${a.nextAvailable.time}` : '9';
    const kb = b.nextAvailable ? `${b.nextAvailable.date}${b.nextAvailable.time}` : '9';
    return ka.localeCompare(kb);
  });
  const list = isAuthenticated ? sorted : doctors;

  const filters = (
    <>
      <Chips options={specialties} value={specialty} onChange={setSpecialty} />
    </>
  );

  let content;
  if (loading && !doctors.length) content = <Skeleton count={4} height={150} />;
  else if (error) content = <ErrorState error={error} onRetry={reload} />;
  else if (!list.length) {
    content = (
      <EmptyState icon={<IconStethoscope size={26} />} title="No doctors found">
        Try a different name, specialty or condition.
      </EmptyState>
    );
  } else {
    content = (
      <div className="u-stack">
        {list.map((d) => <DoctorListCard key={d.id} doctor={d} variant={isAuthenticated ? 'member' : 'public'} />)}
      </div>
    );
  }

  if (isAuthenticated) {
    return (
      <div className="page page--with-nav doctors">
        <div className="doctors__top">
          <h1 className="page__title">Find a doctor</h1>
          <div className="doctors__search">
            <SearchField value={query} onChange={setQuery} placeholder="Doctor, specialty, condition" className="u-grow" />
            <button type="button" className="icon-btn icon-btn--boxed" onClick={() => setSortOpen(true)} aria-label="Sort">
              <IconAdjustmentsHorizontal size={22} />
            </button>
          </div>
          {filters}
        </div>
        <div className="page__body">
          <p className="doctors__count">{`${pluralize(list.length, 'doctor')} available`}</p>
          {content}
        </div>
        <BottomSheet
          open={sortOpen}
          onClose={() => setSortOpen(false)}
          title="Sort doctors"
          actions={<Button block onClick={() => setSortOpen(false)}>Done</Button>}
        >
          {SORTS.map((s) => (
            <button
              key={s.key}
              type="button"
              className={`option ${sort === s.key ? 'option--active' : ''}`}
              onClick={() => setSort(s.key)}
            >
              <span className="option__dot" />
              {s.label}
            </button>
          ))}
        </BottomSheet>
        <BottomNav />
      </div>
    );
  }

  return (
    <div className="page page--with-footer doctors">
      <PublicHeader back />
      <div className="doctors__top">
        <h1 className="page__title doctors__title">Our doctors</h1>
        <p className="page__subtitle">Meet the team and see who’s available.</p>
        <SearchField value={query} onChange={setQuery} placeholder="Search by name or specialty" className="u-mt-4" />
        <div className="u-mt-4">{filters}</div>
      </div>
      <div className="page__body">
        <p className="doctors__count">{pluralize(list.length, 'doctor')}</p>
        {content}
      </div>
      <StickyFooter>
        <Button block to={PATHS.login}>Log in to book</Button>
      </StickyFooter>
    </div>
  );
}
